/**
 * StructuredData helper component
 * Adds JSON-LD structured data to the page head for SEO
 * @module helpers/StructuredData
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from '@plone/volto/helpers';

/**
 * Event structured data component
 * Implements https://schema.org/Event
 */
export const EventStructuredData = ({ content, location }) => {
  if (!content) return null;

  // Build image URL properly
  let imageUrl = null;
  if (content.preview_image) {
    if (content.preview_image.download) {
      const imagePath = content.preview_image.download;
      imageUrl = imagePath.startsWith('http')
        ? imagePath
        : `${location?.origin || ''}${imagePath}`;
    } else if (content.preview_image.scales?.preview?.download) {
      const imagePath = content.preview_image.scales.preview.download;
      imageUrl = imagePath.startsWith('http')
        ? imagePath
        : `${location?.origin || ''}${imagePath}`;
    }
  } else if (content.image) {
    if (content.image.download) {
      const imagePath = content.image.download;
      imageUrl = imagePath.startsWith('http')
        ? imagePath
        : `${location?.origin || ''}${imagePath}`;
    } else if (content.image.scales?.preview?.download) {
      const imagePath = content.image.scales.preview.download;
      imageUrl = imagePath.startsWith('http')
        ? imagePath
        : `${location?.origin || ''}${imagePath}`;
    }
  }

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: content.title || '',
    description: content.description || '',
    // eventStatus is required by Google
    eventStatus: 'https://schema.org/EventScheduled',
    ...(content.start && {
      startDate: content.start,
    }),
    ...(content.end && {
      endDate: content.end,
    }),
    // Location with address
    location: {
      '@type': 'Place',
      name: content.university?.[0]?.title || content.location || content.title || 'Event location',
      address: {
        '@type': 'PostalAddress',
        addressLocality: content.location || 'TBD',
      },
    },
    ...(content.event_url && {
      url: content.event_url.startsWith('http')
        ? content.event_url
        : `${location?.origin || ''}${content.event_url}`,
    }),
    // Organizer - always include with fallback
    organizer: (content.contact_name || content.contact_email || content.contact_phone)
      ? {
          '@type': 'Organization',
          ...(content.contact_name && { name: content.contact_name }),
          ...(content.contact_email && { email: content.contact_email }),
          ...(content.contact_phone && { telephone: content.contact_phone }),
        }
      : {
          '@type': 'Organization',
          name: content.title || 'Event organizer',
        },
    // Performer - provide default if not available
    performer: {
      '@type': 'Organization',
      name: content.university?.[0]?.title || content.contact_name || content.title || 'Event organizer',
    },
    // Only include image if we have a valid URL
    ...(imageUrl && { image: imageUrl }),
    ...(content.eventlanguage && {
      inLanguage: content.eventlanguage,
    }),
    ...(content.whole_day !== undefined && {
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    }),
  };

  // Remove undefined values
  Object.keys(structuredData).forEach(
    (key) => structuredData[key] === undefined && delete structuredData[key],
  );

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
    </Helmet>
  );
};

EventStructuredData.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    start: PropTypes.string,
    end: PropTypes.string,
    location: PropTypes.string,
    location_address: PropTypes.string,
    event_url: PropTypes.string,
    contact_name: PropTypes.string,
    contact_email: PropTypes.string,
    contact_phone: PropTypes.string,
    preview_image: PropTypes.object,
    eventlanguage: PropTypes.string,
    whole_day: PropTypes.bool,
  }),
  location: PropTypes.object,
};

/**
 * NewsArticle structured data component
 * Implements https://schema.org/NewsArticle
 */
export const NewsArticleStructuredData = ({ content, location }) => {
  if (!content) return null;

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: content.title || '',
    ...(content.description && {
      description: content.description,
    }),
    ...(content.created && {
      datePublished: content.created,
    }),
    ...(content.modified && {
      dateModified: content.modified,
    }),
    ...(content.creators && content.creators.length > 0 && {
      author: content.creators.map((creator) => ({
        '@type': 'Person',
        name: creator,
      })),
    }),
    ...(content.preview_image && {
      image: content.preview_image.download
        ? `${location?.origin || ''}${content.preview_image.download}`
        : content.preview_image.scales?.preview?.download
        ? `${location?.origin || ''}${content.preview_image.scales.preview.download}`
        : undefined,
    }),
    ...(content.image && {
      image: content.image.download
        ? `${location?.origin || ''}${content.image.download}`
        : content.image.scales?.preview?.download
        ? `${location?.origin || ''}${content.image.scales.preview.download}`
        : undefined,
    }),
    ...(content['@id'] && {
      url: content['@id'].startsWith('http')
        ? content['@id']
        : `${location?.origin || ''}${content['@id']}`,
    }),
    ...(content.language && {
      inLanguage: content.language.token || content.language,
    }),
  };

  // Remove undefined values
  Object.keys(structuredData).forEach(
    (key) => structuredData[key] === undefined && delete structuredData[key],
  );

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
    </Helmet>
  );
};

NewsArticleStructuredData.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    created: PropTypes.string,
    modified: PropTypes.string,
    creators: PropTypes.array,
    preview_image: PropTypes.object,
    image: PropTypes.object,
    '@id': PropTypes.string,
    language: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  }),
  location: PropTypes.object,
};

/**
 * Article structured data component (generic article)
 * Implements https://schema.org/Article
 */
export const ArticleStructuredData = ({ content, location }) => {
  if (!content) return null;

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: content.title || '',
    ...(content.description && {
      description: content.description,
    }),
    ...(content.created && {
      datePublished: content.created,
    }),
    ...(content.modified && {
      dateModified: content.modified,
    }),
    ...(content.creators && content.creators.length > 0 && {
      author: content.creators.map((creator) => ({
        '@type': 'Person',
        name: creator,
      })),
    }),
    ...(content.preview_image && {
      image: content.preview_image.download
        ? `${location?.origin || ''}${content.preview_image.download}`
        : content.preview_image.scales?.preview?.download
        ? `${location?.origin || ''}${content.preview_image.scales.preview.download}`
        : undefined,
    }),
    ...(content['@id'] && {
      url: content['@id'].startsWith('http')
        ? content['@id']
        : `${location?.origin || ''}${content['@id']}`,
    }),
    ...(content.language && {
      inLanguage: content.language.token || content.language,
    }),
  };

  // Remove undefined values
  Object.keys(structuredData).forEach(
    (key) => structuredData[key] === undefined && delete structuredData[key],
  );

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
    </Helmet>
  );
};

ArticleStructuredData.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    created: PropTypes.string,
    modified: PropTypes.string,
    creators: PropTypes.array,
    preview_image: PropTypes.object,
    '@id': PropTypes.string,
    language: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  }),
  location: PropTypes.object,
};
